import { Address, AddressTo } from '@models/address';
import { BusinessAddress, DATE_FORMAT } from '@models/businesses';
import {
	AssociatedContactType,
	BusinessClient,
	ClientType,
	PersonClient,
	RepresentationType,
} from '@models/clients';
import {
	BusinessMatterClientWithAssociations,
	Capacity,
	CapacityType,
	ERegProperty,
	InterestEstateShort,
	MatterBusinessUser,
	MatterClientAssociations,
	MatterFileType,
	MatterInstrumentPartiesTo,
	MatterTitle,
	PersonMatterClientWithAssociations,
	Statement,
	UserTypeCapacity,
} from '@models/matter';
import { LawFirm } from '@models/lawFirms';
import { format, parse } from 'date-fns';
import * as JsonToXML from 'js2xmlparser';
import { addressString } from './address';
import { getFullName, stringEncode } from './string';
import { clientFormattedName } from './client';

export const generateXML = (objectToXml) =>
	JsonToXML.parse('Import', objectToXml, {
		dtd: { include: true, name: 'Import', sysId: 'import.dtd' },
		declaration: {
			include: true,
			encoding: 'UTF-8',
			standalone: 'no',
			version: '1.0',
		},
	});

export const getProperty = (
	title: MatterTitle,
	titles: MatterTitle[],
	interestEstate: string,
): ERegProperty => {
	let first = '';
	let second = '';

	if (title.pinNumber) {
		[first, second] = title.pinNumber.split('-');
	} else if (titles && titles.length > 0) {
		const primaryPin = titles.find((x) => x.primary === true);
		if (primaryPin && primaryPin.pinNumber) {
			[first, second] = primaryPin.pinNumber.split('-');
		}
	}

	return {
		PropertyBlock: first || '',
		PropertyNumber: second || '',
		InterestEstate: interestEstate ? InterestEstateShort[interestEstate] : '',
	};
};

export const getPartiesTo = (
	buyers: (
		| PersonMatterClientWithAssociations
		| BusinessMatterClientWithAssociations
	)[],
	propertyAddress: Address,
	capacity: Capacity,
): MatterInstrumentPartiesTo[] =>
	buyers?.map((x) => {
		const partyTo: Partial<MatterInstrumentPartiesTo> = {};

		if (x.type === ClientType.PERSON) {
			partyTo.FullName = clientFormattedName(x);
		} else {
			partyTo.FullName = clientFormattedName(x);
		}

		if (capacity) {
			if (capacity.shares[x.id]) {
				if (capacity.capacityType === CapacityType.TENANTS_IN_COMMON) {
					partyTo.Share = `as to a ${capacity.shares[x.id]}% interest`;
				} else if (capacity.capacityType === CapacityType.CAPACITY_PER_CLIENT) {
					partyTo.Share = `${capacity.shares[x.id]}`;
				}
			}
		}

		if (x.type === ClientType.PERSON) {
			if ((x as PersonClient)?.dateOfBirth?.length > 0) {
				const _dob = parse(
					(x as PersonClient).dateOfBirth,
					DATE_FORMAT,
					new Date(),
				);
				partyTo.DateOfBirth = format(_dob, 'yyyy/MM/dd');
			} else {
				partyTo.DateOfBirth = '0000/00/00';
			}
		}

		partyTo.AddressForService = stringEncode(
			addressString(getPartyToAddress(x, propertyAddress)),
		);

		if (capacity) {
			if (
				capacity.capacityType !== CapacityType.SILENT &&
				capacity.capacityType !== CapacityType.CAPACITY_PER_CLIENT
			) {
				partyTo.UserCapacity = UserTypeCapacity[capacity.capacityType];
			} else if (
				capacity.capacityType === CapacityType.CAPACITY_PER_CLIENT &&
				capacity.otherCapacity &&
				capacity.otherCapacity[x.id]
			) {
				partyTo.UserCapacity = UserTypeCapacity[capacity.otherCapacity[x.id]];
			}
		}

		return partyTo as MatterInstrumentPartiesTo;
	});

export const getPartyToAddress = (
	buyer:
		| PersonMatterClientWithAssociations
		| BusinessMatterClientWithAssociations,
	propertyAddress: Address,
): AddressTo => {
	const address = getContactAddress(buyer);

	if (address) {
		return address;
	}

	return propertyAddress;
};

export const getPurchaserOrBorrowerAddress = (
	purchasersOrBorrowers: (
		| PersonMatterClientWithAssociations
		| BusinessMatterClientWithAssociations
	)[],
	propertyAddress: Address,
): AddressTo => {
	let address: AddressTo;

	if (purchasersOrBorrowers && purchasersOrBorrowers.length > 0) {
		let primaryContact = purchasersOrBorrowers.find(
			(x) => x.primaryContact === true,
		);

		if (!primaryContact) {
			primaryContact = purchasersOrBorrowers[0];
		}

		address = getContactAddress(primaryContact);
	}

	if (!address) {
		address = propertyAddress;
	}

	return address;
};

export const getVendorAddress = (
	sellers: (
		| PersonMatterClientWithAssociations
		| BusinessMatterClientWithAssociations
	)[],
	fileType: MatterFileType,
	assignedLawyer: MatterBusinessUser,
	otherLawFirm: LawFirm,
	businessAddress: BusinessAddress,
): AddressTo | undefined => {
	// Get primary contact and check that it or a signing officer has a service address or reporting address
	const primaryContact = sellers.find(
		(x) =>
			x.primaryContact === true &&
			(x.serviceAddress ||
				x.reportAddress ||
				x.clientAssociations?.some(
					(c) =>
						c.associations?.types[AssociatedContactType.SIGNING_OFFICER] &&
						(c.client.serviceAddress || c.client.reportAddress),
				)),
	);

	// Get a secondary contact and check that it or a signing officer has a service address if there's no primary contact
	const secondaryContact = !primaryContact
		? sellers.find(
				(x) =>
					x.serviceAddress ||
					x.clientAssociations?.some(
						(c) =>
							c.associations.types[AssociatedContactType.SIGNING_OFFICER] &&
							c.client.serviceAddress,
					),
			)
		: undefined;

	// Get a tertiary contact and check that it or a signing officer has a reporting address if there's no primary or secondary contact
	const tertiaryContact =
		!primaryContact && !secondaryContact
			? sellers.find(
					(x) =>
						x.reportAddress ||
						x.clientAssociations?.some(
							(c) =>
								c.associations?.types[AssociatedContactType.SIGNING_OFFICER] &&
								c.client.reportAddress,
						),
				)
			: undefined;

	// Use address of primary contact if available, fallback to a seller with a service address if available, further fallback to seller with a reporting address if available
	const availableContact =
		primaryContact ?? secondaryContact ?? tertiaryContact;

	if (availableContact) {
		const contactAddress = getContactAddress(availableContact);

		if (contactAddress) {
			return contactAddress;
		}
	}

	// Fallback to lawyer's address in a sale file
	if (businessAddress && fileType === MatterFileType.SALE) {
		const lawyerAddress = {
			...businessAddress,
			careOf: assignedLawyer?.name || null,
		};
		return lawyerAddress;
	}

	// Fallback to other lawyer's address in a purchase file
	if (otherLawFirm && fileType === MatterFileType.PURCHASE) {
		const vendorsLawyerName = getFullName([
			otherLawFirm.lawyer?.firstName ?? '',
			otherLawFirm.lawyer?.lastName ?? '',
		]);

		const otherLawFirmAddress = {
			...otherLawFirm?.address,
			careOf: vendorsLawyerName || null,
		};

		return otherLawFirmAddress;
	}
};

export const getContactAddress = (
	client:
		| PersonMatterClientWithAssociations
		| BusinessMatterClientWithAssociations,
): AddressTo => {
	if (client.type === ClientType.PERSON) {
		if (
			(client as PersonClient)?.representation === RepresentationType.NONE ||
			!client.clientAssociations.length ||
			!client.clientAssociations.find(
				(clientAssociation) => clientAssociation.associations.primaryContact,
			)
		) {
			if (client?.serviceAddress) {
				return { ...client?.serviceAddress };
			}

			if (client?.reportAddress) {
				return { ...client?.reportAddress };
			}
			return null;
		}

		const contactType =
			(client as PersonClient)?.representation === RepresentationType.ESTATE
				? AssociatedContactType.ESTATE_TRUSTEE
				: AssociatedContactType.POWER_OF_ATTORNEY;
		return getAssociatedServiceAddress(client.clientAssociations, contactType);
	}

	// Use service address for non-person clients if that's available
	if (client.serviceAddress) {
		const signingOfficerName = getAssociatedSigningOfficerName(
			client.clientAssociations,
		);

		if (signingOfficerName) {
			return { ...client?.serviceAddress, careOf: signingOfficerName };
		} else {
			return client.serviceAddress;
		}
	}

	// If non-person client's service address is unavailable, use the service or reporting address of a signing officer if that's available
	const signingOfficerAddress = getAssociatedServiceAddress(
		client.clientAssociations,
		AssociatedContactType.SIGNING_OFFICER,
	);

	if (signingOfficerAddress) {
		return signingOfficerAddress;
	}

	// If there's no client service address or signing officer address available, use the non-person client's report address if that's available
	if (client.reportAddress) {
		const signingOfficerName = getAssociatedSigningOfficerName(
			client.clientAssociations,
		);

		if (signingOfficerName) {
			return { ...client?.reportAddress, careOf: signingOfficerName };
		} else {
			return client.reportAddress;
		}
	}

	return null;
};

export const getStatement = (
	sellers: (
		| PersonMatterClientWithAssociations
		| BusinessMatterClientWithAssociations
	)[],
): Statement => {
	const statement: Statement = { StatementNumber: '' };

	let primary = sellers?.find((x) => x.primaryContact);

	if (!primary && sellers?.length > 0) {
		primary = sellers[0];
	}

	if (!primary) {
		return null;
	}

	if (primary.type !== ClientType.PERSON) {
		statement.StatementNumber = '10';
	} else if (
		(primary as PersonClient)?.representation === RepresentationType.ATTORNEY
	) {
		return null;
	} else {
		statement.StatementNumber = '2909';
	}

	return statement;
};

// Try to get the service address of the best-matched association if available, otherwise fallback to the reporting address
export const getAssociatedServiceAddress = (
	associations: MatterClientAssociations[],
	relationship: AssociatedContactType,
): AddressTo => {
	const primaryContact = associations.find(
		(x) =>
			x.associations.types[relationship] &&
			x.associations.primaryContact &&
			(x.client.serviceAddress || x.client?.reportAddress),
	);

	const secondaryContact = !primaryContact
		? associations.find(
				(x) => x.associations.types[relationship] && x.client.serviceAddress,
			)
		: undefined;

	const tertiaryContact =
		!primaryContact && !secondaryContact
			? associations.find(
					(x) => x.associations.types[relationship] && x.client?.reportAddress,
				)
			: undefined;

	if (!primaryContact && !secondaryContact && !tertiaryContact) return null;

	const availableContact =
		primaryContact ?? secondaryContact ?? tertiaryContact;
	const contactAddress =
		availableContact.client.serviceAddress ??
		availableContact.client.reportAddress;

	return {
		...contactAddress,
		careOf: clientFormattedName(availableContact.client),
	};
};

export const getClientName = (client: PersonClient | BusinessClient) => {
	if (client.type === ClientType.PERSON) {
		return stringEncode(
			`${(client as PersonClient).lastName}, ${(client as PersonClient).firstName} ${(client as PersonClient).middleNames}`,
		)
			?.trim()
			?.toUpperCase();
	}

	return (client as BusinessClient)?.name?.trim().toUpperCase();
};

export const getAssociatedSigningOfficerName = (
	associations: MatterClientAssociations[],
): null | string => {
	const primarySigningOfficer = associations.find(
		(x) =>
			x.associations.types[AssociatedContactType.SIGNING_OFFICER] &&
			x.associations.primaryContact,
	);
	const secondarySigningOfficer = !primarySigningOfficer
		? associations.find(
				(x) => x.associations.types[AssociatedContactType.SIGNING_OFFICER],
			)
		: undefined;

	if (!primarySigningOfficer && !secondarySigningOfficer) {
		return null;
	}

	const availableSigningOfficer =
		primarySigningOfficer ?? secondarySigningOfficer;
	return clientFormattedName(availableSigningOfficer.client);
};
